import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import getBanners from "../api/banner/getBanners";

// ✅ Styled slider with custom dot colors
const StyledSlider = styled(Slider)(() => ({
  ".slick-dots li div": {
    backgroundColor: "rgba(255,255,255,0.6)",
    width: 9,
    height: 9,
    borderRadius: "50%",
    transition: "all 0.3s ease",
  },
  ".slick-dots li.slick-active div": {
    backgroundColor: "#02a4e3",
    width: 10,
    height: 10,
  },
}));

// Helper function to check if a file is a video
const isVideoFile = (url) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.endsWith(ext));
};

const ImageCarousel = ({ storeId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getBanners(storeId);

        const combinedData = data.Link.map((image, index) => ({
          src: image,
          link: (process.env.REACT_APP_API_ENDPOINT || "") + data.Link[index],
          isVideo: isVideoFile(data.Link[index]),
        }));

        setImages(combinedData);
      } catch (err) {
        setError("Failed to fetch banners");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [storeId]);

  const handleOpenFullscreen = (index) => {
    setActiveIndex(index);
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  // Handle video end - go to next slide
  const handleVideoEnded = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  // Check if current slide is a video (to disable autoplay)
  const isCurrentSlideVideo = images[currentSlide]?.isVideo || false;

  const mainSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !isCurrentSlideVideo, // Disable autoplay for videos
    autoplaySpeed: 6000,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: (dots) => (
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          display: "flex",
          justifyContent: "center",
          right: "20px",
          width: "100%",
        }}
      >
        <ul style={{ margin: "0px" }}> {dots} </ul>
      </div>
    ),
    customPaging: () => <div></div>,
  };

  const fullscreenSettings = {
    dots: true,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    // key 讓 Slider 重新 mount，以套用新的 initialSlide
    initialSlide: activeIndex,
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* 原本頁面上的小 Carousel */}
      <Box height="33vh" maxWidth="100%" overflow="hidden">
        <StyledSlider ref={sliderRef} {...mainSettings}>
          {images.map((image, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="center"
              alignItems="center"
              maxWidth="100%"
              onClick={() => handleOpenFullscreen(index)}
              sx={{ cursor: "pointer" }}
            >
              {image.isVideo ? (
                <video
                  src={image.link}
                  autoPlay
                  muted
                  playsInline
                  onEnded={handleVideoEnded}
                  style={{
                    width: "100%",
                    height: "33vh",
                    maxHeight: "33vh",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <img
                  src={image.link}
                  alt={`Image ${index}`}
                  style={{
                    width: "100%",
                    height: "33vh",
                    maxHeight: "33vh",
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>
          ))}
        </StyledSlider>
      </Box>

      {/* 全螢幕 Lightbox Carousel */}
      {fullscreenOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bgcolor="rgba(0,0,0,0.9)"
          zIndex={1300}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <IconButton
            onClick={handleCloseFullscreen}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#fff",
              zIndex: 1301,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box width="100%" maxWidth="900px">
            <StyledSlider
              key={activeIndex} // 依 activeIndex 重新 mount，確保 initialSlide 正確
              {...fullscreenSettings}
            >
              {images.map((image, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                >
                  {image.isVideo ? (
                    <video
                      src={image.link}
                      controls
                      autoPlay
                      loop
                      playsInline
                      style={{
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <img
                      src={image.link}
                      alt={`Image ${index}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              ))}
            </StyledSlider>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ImageCarousel;
